import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ICONS from "../../../../../assets/Icons";
import PrimaryButton from "../../../../../components/_utils/CommonButtons/PrimaryButton";
import SmallPopup from "../../../../../components/_utils/CommonSmallPopup";
import PrimaryStepper from "../../../../../components/_utils/CommonSteppers/PrimaryStepper";
import CommonToast from "../../../../../components/_utils/CommonToast";
import SVGComponent from "../../../../../components/_utils/SVGComponent";
import NameFieldCard from "../../../../../components/_utils/TransactionCommonComponents/NameFieldCard";
import RemitAccountViewAllPopup from "../../../../../components/Accounts/RemitAccount/RemitAccountViewAllPopup";
import Loader from "../../../../../components/Loader/Index";
import {
  remitterAccountDetailsById,
  remitterAccountEditOtherDetails,
  remitterAccountViewAll,
  setRemitterAccountDetailsData,
} from "../../../../../redux/features/accounts/remitterAccount/remitterAccount";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";
import ConfirmBtnAccCreation from "../../../../../components/_utils/AccountCreationComponents/ConfirmBtnAccCreation";

const RemitterAccountEditName = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [payload, setPayload] = useState({
    name: "",
  });
  const [openPopEdit, setOpenPopEdit] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [updateActive, setUpdateActive] = useState(false);
  const [isUpdateEnabled, setIsUpdateEnabled] = useState(false);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [isCheckEnabled, setIsCheckEnabled] = useState(false);
  const [openSimilarPopup, setOpenSimilarPopup] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [checkSimilarAccount, setCheckSimilarAccount] = useState(false);

  const {
    remitterAccountDetailsData,
    remitterAccountViewAllLoading,
    remitterAccountDetailsLoading,
  } = useSelector((state) => state.remitterAccount);

  const originalName = remitterAccountDetailsData?.name || "";
  const rawName = payload?.name || "";

  const cleanedOriginal = originalName.replace(/\s+/g, "").toLowerCase();
  const cleanedCurrent = rawName.replace(/\s+/g, "").toLowerCase();
  const isNameChanged =
    cleanedOriginal !== cleanedCurrent &&
    rawName.trim() !== originalName.trim();

  const nameFieldMode = isNameChanged ? "strict" : "soft";

  const similarStatus = checkSimilarAccount ? isDuplicate : null;
  const isCheckDisabled = !isCheckEnabled;

  const dataArray = [
    {
      previousText: "Cancel",
      nextText: "Update",
      previousEnableBgColor: "var(--primary-nav-act)",
      previousDisableBgColor: "var(--primary-nav-act)",
      previousEnableTextColor: "var(--text-primary)",
      previousHoverOption: "white",
      nextHoverOption: "black",
      nextIsEnabled: isUpdateEnabled,
      previousIsDisabled: updateActive,
      nextIsLoading: updateActive,
      nextLoaderNeeded: true,
      nextEnableBgColor: "var(--positive-act)",
      nextDisableBgColor: updateActive
        ? "var(--positive-act)"
        : "var(--text-disabled)",
      nextEnableTextColor: "var(--text-primary)",
      nextDisableTextColor: "var(--text-primary)",
      stepperText: "Account name",
    },
  ];

  const handleBack = () => {
    if (!hasUnsavedChanges) {
      navigate(
        `/remitter_account_dashboard/remitter_account_view_all/remitter_account_detail_page/${id}`,
      );
    } else {
      setOpenPopEdit(true);
    }
  };

  const handleConfirm = () => {
    navigate(
      `/remitter_account_dashboard/remitter_account_view_all/remitter_account_detail_page/${id}`,
    );
  };

  const checkDuplicateAccount = async () => {
    const trimmedName = payload?.name?.trim() || "";

    const originalName = remitterAccountDetailsData?.name?.trim() || "";

    const hasMinCharacters =
      trimmedName.length >= GlobalConfiguration.lengthCheckThree;

    const isSameAsOriginal = trimmedName === originalName;

    // Invalid name
    if (!hasMinCharacters || isSameAsOriginal) {
      setIsCheckEnabled(false);
      setIsUpdateEnabled(false);
      return false;
    }

    try {
      setDuplicateLoading(true);

      const response = await dispatch(
        remitterAccountViewAll({
          search_string: trimmedName,
        }),
      );

      const accounts =
        response?.payload?.remitter_accounts?.filter(
          (item) => String(item?.id) !== String(id),
        ) || [];

      const duplicateFound =
        accounts.length > GlobalConfiguration.lengthCheckZero;

      setIsDuplicate(duplicateFound);
      setCheckSimilarAccount(true);

      if (duplicateFound) {
        setIsUpdateEnabled(false);
      } else {
        setIsUpdateEnabled(true);
      }

      return duplicateFound;
    } catch (error) {
      console.error("Duplicate check error:", error);

      setIsDuplicate(false);
      setIsUpdateEnabled(false);

      return false;
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleUpdateAccount = async () => {
    if (!payload.name || payload.name.trim() === "") return;

    setUpdateActive(true);

    const response = await dispatch(
      remitterAccountEditOtherDetails({
        id,
        name: payload.name.trim(),
      }),
    );

    setUpdateActive(false);

    if (response?.payload?.status === true) {
      dispatch(
        setRemitterAccountDetailsData({
          ...remitterAccountDetailsData,
          name: payload.name.trim(),
        }),
      );

      navigate(
        `/remitter_account_dashboard/remitter_account_view_all/remitter_account_detail_page/success/${id}`,
      );
    } else if (response?.payload) {
      CommonToast({
        text: response?.payload,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
      });
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(remitterAccountDetailsById({ id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (remitterAccountDetailsData) {
      setPayload({
        name: remitterAccountDetailsData?.name || "",
      });
    }
  }, [remitterAccountDetailsData]);

  return (
    <>
      {openSimilarPopup && (
        <RemitAccountViewAllPopup
          remName={payload?.name?.trim()}
          id={id}
          type={"RemitterAcc"}
          headerTitle={"Similar accounts"}
          propType={"similerAccount"}
          headersubtext={
            "The following accounts have similar names and require a unique name before updating the remitter account."
          }
          closePopup={setOpenSimilarPopup}
          onCardNavigate
        />
      )}

      {openPopEdit && (
        <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
          <SmallPopup
            headerTitle="Confirm leaving this page?"
            subtext="Leaving this page will not update the changes made."
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-md"
            btnOneText="Stay"
            btnOneBtnType="button"
            btnOneEnabledBgColor="transparent"
            btnOneHandleFunction={() => setOpenPopEdit(false)}
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText="Leave"
            btnTwoBtnType="button"
            btnTwoHandleFunction={handleConfirm}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoHoverOption="black"
          />
        </div>
      )}

      {/* {duplicateLoading && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-fixed-ui bg-PopUp-BackDrop opacity-dark-opacity">
          <div>
            <Loader type={"bulk_upload"} />
            <p className="font-semibold text-font-lg text-text-primary">
              Validating the data
            </p>
          </div>
        </div>
      )} */}

      {remitterAccountDetailsLoading ? (
        <div className="absolute top-top-base z-z-index-fixed-ui h-height-full w-width-full flex justify-center items-center">
          <Loader />
        </div>
      ) : (
        <div className="w-width-full flex flex-col items-center h-full-height bg-primary-bg">
          <div className="w-width-full Qlg:bg-transparent bg-primary-nav rounded-b-border-radius-md">
            <div className="h-navbar-height hidden Qsmd:block"></div>
            <div className="h-[calc(var(--navbar-breadcrumb-height)-var(--navbar-height))] mb-margin-sm mt-margin-xs Qsmd:my-margin-base"></div>
          </div>

          <div className="flex justify-center items-end !bottom-bottom-base rounded-t-border-radius-md Qlg:bg-transparent bg-primary-nav rounded-b-border-radius-md w-screen fixed Qsmd:relative Qsmd:top-top-base left-left-base z-z-index-header flex-shrink-0 h-[var(--padding-bottom-unassign-cat)]">
            <PrimaryStepper
              activeStep={0}
              handleForwardAction={handleUpdateAccount}
              handleBackwardAction={handleBack}
              dataArray={dataArray}
            />
          </div>

          <div
            className="flex items-center w-width-full py-padding-sm px-padding-md h-mobile-create-header fixed left-left-base top-top-base z-z-index-header Qsmd:hidden"
            onClick={handleBack}
          >
            <SVGComponent
              color="var(--text-primary)"
              height="var(--icon-sm)"
              width="var(--icon-sm)"
              url={ICONS?.ARROW_LEFT_WHITE}
              alt="Icon"
            />
            <div className="flex flex-col pl-padding-md gap-gap-xs">
              <p className="text-font-md font-sans text-text-primary">
                Remitter Account
              </p>
              <p className="text-xs font-sans text-text-primary">
                Account name
              </p>
            </div>
          </div>

          <div className="w-full flex flex-col lg:flex-row justify-center gap-gap-xl pt-padding-md px-padding-md lg:px-padding-base">
            <div className="flex flex-col Qsmd:flex-row w-full gap-gap-md Qsmd:gap-gap-xl max-w-max-content-width">
              {" "}
              <div className="w-full Qsmd:max-w-[var(--create-box-width)]">
                <div>
                  <NameFieldCard
                    title="Account Name"
                    placeholder="Account name"
                    fieldName="remitter_name"
                    value={payload.name}
                    note={"Note: Minimum 3 characters required."}
                    mode={nameFieldMode}
                    minLength={GlobalConfiguration?.lengthCheckThree}
                    onChange={(val) => {
                      const cleanedName = val.trimStart();

                      const originalName =
                        remitterAccountDetailsData?.name?.trim() || "";
                      const trimmedName = cleanedName.trim();

                      const hasMinCharacters =
                        cleanedName.replace(/\s+/g, "").length >=
                        GlobalConfiguration.lengthCheckThree;
                      const isSameAsOriginal = trimmedName === originalName;

                      const previousTrimmed = (payload.name || "").trimEnd();
                      const newTrimmed = cleanedName.trimEnd();
                      const contentChanged = newTrimmed !== previousTrimmed;

                      setPayload((prev) => ({
                        ...prev,
                        name: cleanedName,
                      }));

                      setIsCheckEnabled(hasMinCharacters && !isSameAsOriginal);
                      setHasUnsavedChanges(!isSameAsOriginal);

                      if (contentChanged) {
                        setIsDuplicate(null);
                        setOpenSimilarPopup(false);
                        setCheckSimilarAccount(false);
                        setIsUpdateEnabled(false);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="w-full Qsmd:max-w-[var(--create-box-width)]">
                {similarStatus && (
                  <div className="!overflow-hidden">
                    <div className="animate-slideInFromRight">
                      <ConfirmBtnAccCreation
                        type="remitter"
                        status={similarStatus}
                        isConfirmed={isUpdateEnabled}
                        handleClick={() => setIsUpdateEnabled(true)}
                        subtext={
                          "Confirm and click ‘Update’ to proceed with updating the account with a similar name."
                        }
                      />
                    </div>
                  </div>
                )}

                <div
                  className={`flex gap-gap-sm w-full rounded-border-radius-md bg-primary-card p-padding-md ${
                    similarStatus
                      ? "cursor-pointer global-hover-white z-z-index-content relative"
                      : ""
                  }`}
                  onClick={() => {
                    if (similarStatus) setOpenSimilarPopup(true);
                  }}
                >
                  {similarStatus && (
                    <div className="flex-shrink-0">
                      <img
                        src={ICONS?.ALERT}
                        className="w-icon-md h-icon-md"
                        alt="Alert"
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-gap-sm w-width-full">
                    <div className="flex flex-col gap-gap-sm w-width-full">
                      <div className="w-full h-auto font-semibold text-base text-text-primary flex justify-between items-center">
                        {similarStatus === null ? (
                          <h1>Similar Accounts</h1>
                        ) : similarStatus === false ? (
                          <h1>No Similar Accounts Found</h1>
                        ) : (
                          <h1>Similar Accounts Found</h1>
                        )}

                        {similarStatus && (
                          <SVGComponent
                            url={ICONS.ARROW_RIGHT_WHITE}
                            height={"var(--icon-sm)"}
                            width={"var(--icon-sm)"}
                            color={"var(--text-primary)"}
                          />
                        )}
                      </div>

                      {!duplicateLoading && (
                        <ul
                          className={`w-full h-auto font-normal text-xs text-text-secondary flex flex-col gap-gap-sm ${
                            similarStatus ? "list-disc pl-padding-md" : ""
                          }`}
                        >
                          <li>
                            {similarStatus === null
                              ? 'Click "Check" to view similar accounts.'
                              : similarStatus === false
                                ? "There are no existing accounts that are similar to the provided name."
                                : "Review existing accounts that are similar to the provided name before updating this account."}
                          </li>
                        </ul>
                      )}
                    </div>

                    {similarStatus === null && (
                      <div className="w-width-full">
                        <PrimaryButton
                          btnText="Check"
                          btnType="button"
                          hoverOption="white"
                          handleFunction={() => {
                            if (isCheckEnabled) checkDuplicateAccount();
                          }}
                          enabledTextColor={"var(--text-primary)"}
                          disabledTextColor={"var(--text-primary)"}
                          enabledBgColor={"var(--positive-act)"}
                          disabledBgColor={"var(--text-disabled)"}
                          isDisabled={isCheckDisabled}
                          isLoading={
                            remitterAccountViewAllLoading && duplicateLoading
                          }
                          isLoaderNeeded
                          isFillNeeded
                          inActiveVariantNeeded
                          fullWidthRequired
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="w-full max-w-[var(--create-box-width)] invisible lg:visible"></div> */}
          </div>
        </div>
      )}
    </>
  );
};

export default RemitterAccountEditName;
